import { useEffect, useRef } from 'react';

interface Props {
  src: string;
  className?: string;
  poster?: string;
  /** Slightly faster forward/reverse (catalog tiles used 1.12 on hover). */
  playbackRate?: number;
  onPlayingChange?: (playing: boolean) => void;
}

function revSrc(src: string) {
  return src.replace(/\.mp4$/, '_rev.mp4');
}

const LAYER_CLASS = 'absolute inset-0 w-full h-full object-cover';

/**
 * Ping-pong via pre-rendered reverse MP4 + native decode (smooth on Mac/Safari).
 * Falls back to rAF scrub only if the reverse file fails to load.
 */
export default function PingPongVideo({
  src,
  className = '',
  poster,
  playbackRate = 1.12,
  onPlayingChange,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const fwdRef = useRef<HTMLVideoElement>(null);
  const revRef = useRef<HTMLVideoElement>(null);
  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;

  useEffect(() => {
    const fwd = fwdRef.current;
    const rev = revRef.current;
    if (!fwd || !rev) return;

    let cancelled = false;
    let rafId = 0;
    let lastTs = 0;
    let reversing = false;
    let nativeReverse = true;
    let revPrimed = false;

    const setPlaying = (playing: boolean) => {
      if (!cancelled) onPlayingChangeRef.current?.(playing);
    };

    const showFwd = () => {
      fwd.style.opacity = '1';
      fwd.style.zIndex = '2';
      rev.style.opacity = '0';
      rev.style.zIndex = '1';
    };

    const showRev = () => {
      fwd.style.opacity = '0';
      fwd.style.zIndex = '1';
      rev.style.opacity = '1';
      rev.style.zIndex = '2';
    };

    const applyRate = (v: HTMLVideoElement) => {
      v.playbackRate = playbackRate;
    };

    const stopRaf = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      lastTs = 0;
      reversing = false;
    };

    const rafReverse = (ts: number) => {
      if (!reversing || cancelled) return;
      const delta = lastTs ? Math.min((ts - lastTs) / 1000, 0.05) : 1 / 60;
      lastTs = ts;
      const next = Math.max(0, fwd.currentTime - delta);
      fwd.currentTime = next;
      if (next <= 0) {
        stopRaf();
        fwd.currentTime = 0;
        void startForward();
      } else {
        rafId = requestAnimationFrame(rafReverse);
      }
    };

    const startForward = async () => {
      stopRaf();
      revPrimed = false;
      showFwd();
      rev.pause();
      try {
        rev.currentTime = 0;
      } catch {
        /* ignore */
      }
      try {
        fwd.currentTime = 0;
      } catch {
        /* ignore */
      }
      applyRate(fwd);
      try {
        await fwd.play();
        if (!cancelled) setPlaying(true);
      } catch {
        setPlaying(false);
      }
    };

    const handoffToReverse = () => {
      stopRaf();
      revPrimed = false;
      showRev();
      fwd.pause();
      applyRate(rev);
      if (rev.paused) {
        try {
          if (rev.currentTime > 0.2) rev.currentTime = 0;
        } catch {
          /* ignore */
        }
        void rev.play().catch(() => {
          nativeReverse = false;
          startRafReverse();
        });
      }
    };

    const startRafReverse = () => {
      stopRaf();
      showFwd();
      rev.pause();
      reversing = true;
      lastTs = 0;
      rafId = requestAnimationFrame(rafReverse);
    };

    const primeReverse = () => {
      if (!nativeReverse || revPrimed || cancelled) return;
      revPrimed = true;
      try {
        rev.currentTime = 0;
      } catch {
        /* ignore */
      }
      applyRate(rev);
      void rev.play().catch(() => {
        revPrimed = false;
        nativeReverse = false;
      });
    };

    const onFwdTimeUpdate = () => {
      if (!nativeReverse || !fwd.duration) return;
      if (fwd.currentTime >= fwd.duration - 0.12) primeReverse();
    };

    const onFwdEnded = () => {
      if (cancelled) return;
      if (nativeReverse) handoffToReverse();
      else startRafReverse();
    };

    const onRevEnded = () => {
      if (cancelled) return;
      void startForward();
    };

    const onRevError = () => {
      nativeReverse = false;
      revPrimed = false;
    };

    const onPlaying = () => setPlaying(true);

    fwd.addEventListener('ended', onFwdEnded);
    fwd.addEventListener('timeupdate', onFwdTimeUpdate);
    fwd.addEventListener('playing', onPlaying);
    rev.addEventListener('ended', onRevEnded);
    rev.addEventListener('error', onRevError);

    void startForward();

    return () => {
      cancelled = true;
      fwd.removeEventListener('ended', onFwdEnded);
      fwd.removeEventListener('timeupdate', onFwdTimeUpdate);
      fwd.removeEventListener('playing', onPlaying);
      rev.removeEventListener('ended', onRevEnded);
      rev.removeEventListener('error', onRevError);
      stopRaf();
      fwd.pause();
      rev.pause();
      setPlaying(false);
    };
  }, [src, playbackRate]);

  return (
    <div ref={wrapRef} className={`relative w-full h-full ${className}`}>
      <video
        ref={fwdRef}
        src={src}
        poster={poster}
        muted
        playsInline
        preload="auto"
        className={LAYER_CLASS}
        style={{ opacity: 1, zIndex: 2 }}
      />
      <video
        ref={revRef}
        src={revSrc(src)}
        muted
        playsInline
        preload="auto"
        className={LAYER_CLASS}
        style={{ opacity: 0, zIndex: 1 }}
      />
    </div>
  );
}
